// src/components/vendor/StoreModal.tsx

import React, { useState, useEffect, useRef } from 'react';
import {
    X, Store, MapPin, Mail, Phone, Globe,
    Upload, AlertCircle, Check, ChevronDown, Pencil,
    User
} from 'lucide-react';
import AddressForm from '../address/AddressForm';
import type { Store as StoreType } from '../../types';
import { formatPhoneNumber } from '../../utils/phoneFormatter';
import addressAPI from '../../api/addresses';

interface StoreModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => Promise<void>;
    initialData?: StoreType;
    isProcessing?: boolean;
    vendorName?: string;
    vendorType?: string;
}

const StoreModal: React.FC<StoreModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    initialData,
    isProcessing = false,
    vendorName = '',
    vendorType = 'individual',
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        logo: null as File | null,
        contact_email: '',
        contact_phone: '',
        website: '',
    });

    const [storeAddress, setStoreAddress] = useState<any>(
        initialData?.store_address_detail || null
    );
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [suggestedNames, setSuggestedNames] = useState<string[]>([]);
    const [showNameSuggestions, setShowNameSuggestions] = useState(false);
    const [cities, setCities] = useState<Array<{ id: number; name: string; delivery_fee: number }>>([]);
    const nameRef = useRef<HTMLDivElement>(null);
    const addressRef = useRef<HTMLDivElement>(null);
    const contactRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (vendorName && !initialData) {
            const names = generateStoreNames(vendorName, vendorType);
            setSuggestedNames(names);
            if (vendorType === 'individual' && names.length > 0) {
                setFormData(prev => ({ ...prev, name: names[0] }));
            }
        }
    }, [vendorName, vendorType, initialData]);

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name || '',
                description: initialData.description || '',
                logo: null,
                contact_email: initialData.contact_email || '',
                contact_phone: initialData.contact_phone || '',
                website: initialData.website || '',
            });
            setStoreAddress(initialData.store_address_detail || null);
            if (initialData.logo) setLogoPreview(initialData.logo);
        }
    }, [initialData]);

    useEffect(() => {
        if (isOpen) {
            const fetchCities = async () => {
                try {
                    const response = await addressAPI.getCities();
                    if (response.data) {
                        setCities(response.data);
                    }
                } catch (err) {
                    console.error('Failed to fetch cities:', err);
                }
            };
            fetchCities();
        }
    }, [isOpen]);

    const generateStoreNames = (name: string, type: string): string[] => {
        const parts = name.trim().split(' ');
        const firstName = parts[0] || '';
        const lastName = parts[parts.length - 1] || '';
        const fullName = name.trim();

        if (type === 'individual') {
            return [
                `${firstName}'s Store`,
                `${lastName}'s Store`,
                `${firstName} ${lastName}'s Store`,
                `${firstName.charAt(0)}.${lastName.charAt(0)}. Store`,
                `${fullName}`,
            ];
        }
        return [
            `${fullName}`,
            `${fullName} (${type.replace('_', ' ').toUpperCase()})`,
        ];
    };

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => {
                const n = { ...prev }; delete n[field]; return n;
            });
        }
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setFormData(prev => ({ ...prev, logo: file }));
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const getErrorClass = (field: string) =>
        touched[field] && errors[field] ? 'border-red-500 bg-red-50' : 'border-gray-300';

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        const newTouched: Record<string, boolean> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Store name is required';
            newTouched.name = true;
        }
        // if (!storeAddress) {
        //     newErrors.store_address = 'Business address is required';
        //     newTouched.store_address = true;
        // }
        if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
            newErrors.contact_email = 'Valid email is required';
            newTouched.contact_email = true;
        }
        if (formData.website && !/^https?:\/\/[^\s]+$/.test(formData.website)) {
            newErrors.website = 'Valid URL is required (start with http:// or https://)';
            newTouched.website = true;
        }

        setErrors(newErrors);
        setTouched(prev => ({ ...prev, ...newTouched }));
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const fd = new FormData();

        const payload: any = {
            ...formData,
            store_address: storeAddress?.id || null,
            store_address_data: storeAddress,
        };

        fd.append('data', JSON.stringify(payload));

        if (formData.logo) {
            fd.append('logo', formData.logo);
        }
        await onSubmit(fd);
    };

    const handleSaveStoreAddress = (formData: any) => {
        setStoreAddress({
            ...formData,
            id: storeAddress?.id ?? null,  // preserve existing ID if editing
        });
        setShowAddressForm(false);
        setErrors(prev => {
            const n = { ...prev };
            delete n.store_address;
            return n;
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Store className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {initialData ? 'Edit Store' : 'Create Store'}
                            </h3>
                            <p className="text-sm text-gray-500">
                                {initialData ? 'Update your store information' : 'Set up your store to start selling'}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        disabled={isProcessing}
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Store Name */}
                    <div ref={nameRef} className="scroll-mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Store Name <span className="text-red-500">*</span>
                        </label>

                        {vendorType === 'individual' && !initialData ? (
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setShowNameSuggestions(!showNameSuggestions)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                                >
                                    <span className={formData.name ? 'text-gray-900' : 'text-gray-400'}>
                                        {formData.name || 'Select a store name...'}
                                    </span>
                                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showNameSuggestions ? 'rotate-180' : ''}`} />
                                </button>
                                {showNameSuggestions && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {suggestedNames.map((name, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => { handleChange('name', name); setShowNameSuggestions(false); }}
                                                className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm transition-colors"
                                            >
                                                {name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="Enter your registered business name"
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${getErrorClass('name')}`}
                            />
                        )}
                        {errors.name && (
                            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                <AlertCircle className="h-4 w-4" /> {errors.name}
                            </p>
                        )}
                    </div>

                    {/* Slug (read-only, edit mode) */}
                    {initialData?.slug && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Store URL
                            </label>
                            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                                <Globe className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <code className="truncate">/stores/{initialData.slug}</code>
                            </div>
                            <p className="mt-1 text-xs text-gray-400">
                                Your store URL is permanent and cannot be changed.
                            </p>
                        </div>
                    )}

                    {/* Logo Upload */}
                    {vendorType !== 'individual' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Store Logo (Optional)
                            </label>
                            <div className="flex items-center gap-4">
                                <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                                    {logoPreview ? (
                                        <img src={logoPreview} alt="Logo preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <Store className="h-8 w-8 text-gray-400" />
                                    )}
                                </div>
                                <label className="cursor-pointer px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm text-gray-700">
                                    <Upload className="h-4 w-4 inline mr-2" />
                                    Upload Logo
                                    <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Store Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            rows={2}
                            placeholder="Describe your store, products, and brand..."
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                        />
                    </div>

                    {/* Business Address Card */}
                    <div ref={addressRef} className="scroll-mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Business Address {/* <span className="text-red-500">*</span> */}
                        </label>
                        <button
                            type="button"
                            onClick={() => setShowAddressForm(true)}
                            className={`w-full text-left border rounded-lg p-4 hover:border-blue-400 hover:bg-blue-50/30 transition-colors ${errors.store_address ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                }`}
                        >
                            {storeAddress ? (
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-blue-100 rounded flex-shrink-0">
                                        <MapPin className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <p className="text-sm font-medium text-gray-900">
                                                {storeAddress.label || 'Business'}
                                            </p>
                                            {storeAddress.is_default && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                                    Default
                                                </span>
                                            )}
                                        </div>

                                        {storeAddress.recipient_name && (
                                            <div className="flex items-center text-sm text-gray-700 mb-1">
                                                <User className="h-3.5 w-3.5 text-gray-400 mr-1.5" />
                                                {storeAddress.recipient_name}
                                            </div>
                                        )}

                                        {(storeAddress.contact_number_1 || storeAddress.email) && (
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600 mb-1">
                                                {storeAddress.contact_number_1 && (
                                                    <span className="flex items-center">
                                                        <Phone className="h-3.5 w-3.5 text-gray-400 mr-1.5" />
                                                        {formatPhoneNumber(storeAddress.contact_number_1)}
                                                    </span>
                                                )}
                                                {storeAddress.contact_number_2 && (
                                                    <span className="flex items-center">
                                                        <Phone className="h-3.5 w-3.5 text-gray-400 mr-1.5" />
                                                        {formatPhoneNumber(storeAddress.contact_number_2)}
                                                    </span>
                                                )}
                                                {storeAddress.email && (
                                                    <span className="flex items-center">
                                                        <Mail className="h-3.5 w-3.5 text-gray-400 mr-1.5" />
                                                        {storeAddress.email}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        <div className="text-sm text-gray-600">
                                            {storeAddress.house_number && (
                                                <span className="font-medium">{storeAddress.house_number} </span>
                                            )}
                                            {storeAddress.address_line_1}
                                            {storeAddress.address_line_2 && `, ${storeAddress.address_line_2}`}
                                        </div>
                                        <div className="text-sm text-gray-900 font-medium">
                                            {storeAddress.city_details?.name || storeAddress.city}
                                        </div>

                                        {storeAddress.delivery_note && (
                                            <p className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
                                                <span className="font-medium">Note:</span> {storeAddress.delivery_note}
                                            </p>
                                        )}
                                    </div>
                                    <Pencil className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 text-gray-500">
                                    <MapPin className="h-5 w-5" />
                                    <span className="text-sm">Click to add business address</span>
                                </div>
                            )}
                        </button>
                        {errors.store_address && (
                            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                <AlertCircle className="h-4 w-4" /> {errors.store_address}
                            </p>
                        )}
                    </div>

                    {/* Website */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Website (Optional)
                        </label>
                        <div className="relative">
                            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="url"
                                value={formData.website}
                                onChange={(e) => handleChange('website', e.target.value)}
                                placeholder="https://example.com"
                                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${getErrorClass('website')}`}
                            />
                        </div>
                        {errors.website && (
                            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                <AlertCircle className="h-4 w-4" /> {errors.website}
                            </p>
                        )}
                    </div>

                    {/* Internal Contact */}
                    <div ref={contactRef} className="scroll-mt-4 pt-2 border-t border-gray-200">
                        <p className="text-sm text-gray-500 mb-3">
                            Used by the platform to reach you. Not shown publicly.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Internal Contact Email
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="email"
                                        value={formData.contact_email}
                                        onChange={(e) => handleChange('contact_email', e.target.value)}
                                        placeholder="store@example.com"
                                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${getErrorClass('contact_email')}`}
                                    />
                                </div>
                                {errors.contact_email && (
                                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                        <AlertCircle className="h-4 w-4" /> {errors.contact_email}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Internal Contact Phone
                                </label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="tel"
                                        value={formData.contact_phone}
                                        onChange={(e) => handleChange('contact_phone', e.target.value)}
                                        placeholder="+1 (868) 123-4567"
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isProcessing}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isProcessing}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        {isProcessing ? (
                            <>
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Check className="h-4 w-4" />
                                {initialData ? 'Update Store' : 'Create Store'}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Address Form Modal (nested) */}
            {showAddressForm && (
                <AddressForm
                    onCancel={() => setShowAddressForm(false)}
                    onSubmit={handleSaveStoreAddress}
                    mode="store"
                    cities={cities}
                    showEmailField={true}
                    initialData={storeAddress}
                    onSuccess={(savedAddress: any) => {
                        setStoreAddress(savedAddress);
                        setShowAddressForm(false);
                        setErrors(prev => {
                            const n = { ...prev }; delete n.store_address; return n;
                        });
                    }}
                />
            )}
        </div>
    );
};

export default StoreModal;